import { useEffect, useRef, useState } from 'react';
import { App as AntApp, Button } from 'antd';
import { AsyncButton } from './kit';

/**
 * Draw-to-sign canvas.
 *
 * Lifted out of PublicSigningPage when the co-share form became a third caller —
 * a customer signing an OTP on a remote link, a party signing on the agent's
 * phone, and now a REN signing their own share of a fee split all want the same
 * box. The behaviour is unchanged from the version that was already in
 * production; only the surrounding copy is a prop.
 *
 * Touch and mouse are handled separately on purpose: the touch listeners are
 * registered with `{ passive: false }` so `preventDefault` actually suppresses
 * the page scroll that would otherwise fight the stroke on a phone.
 *
 * Hands back a PNG data URL, which is what both signature columns store.
 */
export default function SignaturePad({
  onConfirm,
  confirmLabel = 'Sign & submit',
  hint = 'sign inside the box',
  height = 190,
  disabled = false,
}) {
  const { message } = AntApp.useApp();
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  const posOf = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return undefined;
    const ctx = c.getContext('2d');
    c.width = c.offsetWidth || 300;
    c.height = height;
    // A white ground, not transparency: the signature is composited onto a
    // printed document, and a transparent PNG over a dark viewer reads as blank.
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const start = (e) => { e.preventDefault(); drawing.current = true; const p = posOf(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move = (e) => { if (!drawing.current) return; e.preventDefault(); const p = posOf(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasDrawn(true); };
    const end = () => { drawing.current = false; };
    c.addEventListener('touchstart', start, { passive: false });
    c.addEventListener('touchmove', move, { passive: false });
    c.addEventListener('touchend', end);
    return () => {
      c.removeEventListener('touchstart', start);
      c.removeEventListener('touchmove', move);
      c.removeEventListener('touchend', end);
    };
  }, [height]);

  const mDown = (e) => { if (disabled) return; drawing.current = true; const ctx = canvasRef.current.getContext('2d'); const p = posOf(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const mMove = (e) => { if (!drawing.current) return; const ctx = canvasRef.current.getContext('2d'); const p = posOf(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasDrawn(true); };
  const mUp = () => { drawing.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, c.width, c.height);
    setHasDrawn(false);
  };

  const confirm = async () => {
    if (!hasDrawn) { message.warning('Please draw your signature first'); return; }
    await onConfirm(canvasRef.current.toDataURL('image/png'));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height, display: 'block', border: '1.5px dashed var(--border)', borderRadius: 11, background: '#fff', touchAction: 'none', cursor: disabled ? 'not-allowed' : 'crosshair' }}
        onMouseDown={mDown}
        onMouseMove={mMove}
        onMouseUp={mUp}
        onMouseLeave={mUp}
      />
      <div style={{ fontSize: 10.5, color: 'var(--muted)', textAlign: 'center', marginTop: -4 }}>{hint}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button onClick={clear} style={{ paddingInline: 16 }}>Clear</Button>
        <AsyncButton block type="primary" disabled={!hasDrawn || disabled} onClick={confirm} onError={(e) => message.error(e.message)}>
          {confirmLabel}
        </AsyncButton>
      </div>
    </div>
  );
}
